import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  AppShell,
  Avatar,
  Badge,
  Box,
  Burger,
  Divider,
  Group,
  Menu,
  NavLink,
  ScrollArea,
  SegmentedControl,
  Stack,
  Text,
  UnstyledButton,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";
import { IconChevronDown, IconKey, IconLogout, IconMoon, IconSun } from "@tabler/icons-react";
import { useAuth } from "../../auth/useAuth";
import { can, canAccess, type ModuleKey } from "../../auth/rbac";
import { adminRoleLabel } from "../../types/admin";
import { useI18n, type Language } from "../../i18n/context";
import { consoleApi } from "../../api/console";
import { NAV, NAV_SECTIONS } from "./nav";
import { HeaderSearch } from "./HeaderSearch";
import { useThemeSwitch } from "./useThemeSwitch";
import { BrandLogo } from "../brand/BrandLogo";
import { SosAlertProvider } from "../common/SosAlertProvider";
import { ChatWidget } from "../chat/ChatWidget";

export function AdminShell() {
  const [opened, { toggle, close }] = useDisclosure();
  const location = useLocation();
  const navigate = useNavigate();
  const { admin, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const { isDark, toggle: toggleTheme } = useThemeSwitch();

  const visible = NAV.filter((item) => canAccess(admin?.role, item.module));
  // Longest matching path wins, so /reports/monthly doesn't also light up /reports.
  const activeModule = visible
    .filter((i) =>
      i.to === "/" ? location.pathname === "/" : location.pathname === i.to || location.pathname.startsWith(`${i.to}/`),
    )
    .sort((a, b) => b.to.length - a.to.length)[0]?.module;

  // Attention counts on the nav. Shares cache keys with the pages, so opening
  // Inbox/Live doesn't refetch what the sidebar just loaded.
  const inbox = useQuery({
    queryKey: ["ops-inbox"],
    queryFn: consoleApi.inbox,
    enabled: can(admin?.role, "dashboard.view"),
    refetchInterval: 60_000,
  });
  const sectionCount = (key: string) =>
    inbox.data?.sections.find((s) => s.key === key)?.count ?? 0;
  const badges: Partial<Record<ModuleKey, { count: number; color: string }>> = {
    inbox: { count: inbox.data?.total ?? 0, color: "orange" },
    live: { count: sectionCount("stuckTrips"), color: "orange" },
    sos: { count: sectionCount("sos"), color: "red" },
  };

  async function handleLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{ width: 256, breakpoint: "sm", collapsed: { mobile: !opened } }}
      padding="lg"
    >
      <a href="#main" className="skip-link">
        {t("shell.skipToContent")}
      </a>
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap" gap="sm">
          <Group gap="sm" wrap="nowrap">
            <Burger
              opened={opened}
              onClick={toggle}
              hiddenFrom="sm"
              size="sm"
              aria-label={t("shell.menu")}
            />
            <UnstyledButton onClick={() => navigate("/")} aria-label={t("nav.home")}>
              <Group gap={10} wrap="nowrap">
                <BrandLogo height={26} />
                <Text fw={500} c="var(--text-secondary)" fz="sm" visibleFrom="md">
                  {t("app.console")}
                </Text>
              </Group>
            </UnstyledButton>
          </Group>

          <Box style={{ flex: 1, display: "flex", justifyContent: "center" }}>
            <HeaderSearch />
          </Box>

          <Group gap="xs" wrap="nowrap">

            <Menu width={230} position="bottom-end" withArrow>
              <Menu.Target>
                <UnstyledButton aria-label={t("shell.account")}>
                  <Group gap="xs" wrap="nowrap">
                    <Avatar color="oxblood" radius="xl" size={32}>
                      {admin?.name?.charAt(0)?.toUpperCase() ?? "A"}
                    </Avatar>
                    <Box visibleFrom="lg">
                      <Text size="sm" fw={600} lh={1.2}>
                        {admin?.name ?? "Admin"}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {adminRoleLabel(admin?.role) ?? t("shell.administrator")}
                      </Text>
                    </Box>
                    <IconChevronDown size={14} />
                  </Group>
                </UnstyledButton>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>{admin?.email}</Menu.Label>
                <Box px="sm" py={6}>
                  <Text size="xs" c="dimmed" mb={4}>
                    {t("shell.language")}
                  </Text>
                  <SegmentedControl
                    fullWidth
                    size="xs"
                    value={lang}
                    onChange={(v) => setLang(v as Language)}
                    data={[
                      { value: "en", label: "English" },
                      { value: "sw", label: "Kiswahili" },
                    ]}
                  />
                </Box>
                <Menu.Divider />
                <Menu.Item
                  leftSection={isDark ? <IconSun size={16} /> : <IconMoon size={16} />}
                  onClick={toggleTheme}
                >
                  {isDark ? t("search.action.lightMode") : t("search.action.darkMode")}
                </Menu.Item>
                <Menu.Item leftSection={<IconKey size={16} />} onClick={() => navigate("/account/password")}>
                  {t("shell.changePassword")}
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item color="red" leftSection={<IconLogout size={16} />} onClick={handleLogout}>
                  {t("shell.logout")}
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="sm" aria-label={t("shell.menu")}>
        <AppShell.Section grow component={ScrollArea}>
          <Stack gap={2}>
            {NAV_SECTIONS.map((section) => {
              const items = visible.filter((i) => i.section === section);
              if (items.length === 0) return null;
              return (
                <Box key={section} mb={6}>
                  <Text size="xs" fw={700} tt="uppercase" px="xs" mb={2} mt={4} className="nav-section-label">
                    {t(`nav.section.${section}`)}
                  </Text>
                  {items.map((item) => {
                    const active = activeModule === item.module;
                    const badge = badges[item.module];
                    return (
                      <NavLink
                        key={item.module}
                        label={t(item.labelKey)}
                        active={active}
                        aria-current={active ? "page" : undefined}
                        leftSection={<item.icon size={18} stroke={1.7} />}
                        rightSection={
                          badge && badge.count > 0 ? (
                            <Badge size="xs" color={badge.color} variant="filled" className="num">
                              {badge.count > 99 ? "99+" : badge.count}
                            </Badge>
                          ) : undefined
                        }
                        onClick={() => {
                          navigate(item.to);
                          close();
                        }}
                        className="nav-item"
                        style={{ borderRadius: 8 }}
                      />
                    );
                  })}
                </Box>
              );
            })}
          </Stack>
        </AppShell.Section>
        <Divider my="xs" />
        <Text size="xs" c="dimmed" px="xs">
          {t("app.name")} {t("app.console")}
        </Text>
      </AppShell.Navbar>

      <AppShell.Main id="main" tabIndex={-1} style={{ outline: "none" }}>
        <SosAlertProvider>
          <Outlet />
        </SosAlertProvider>
      </AppShell.Main>

      <ChatWidget />
    </AppShell>
  );
}
